import { datadogLogs } from '@datadog/browser-logs';
import { useCallback, useEffect, useReducer, useState } from 'react';
import { isAxiosError } from 'src/api/instance';
import proofApi from 'src/features/proofs/api';
import {
  CompensationData,
  ReviewContact
} from 'src/features/proofs/ts/interfaces/OfferLetterVerification';
import {
  OFFER_LETTER_CHECKLIST_STEPS,
  OLV_LLM_FIELD_MAP,
  OLV_REQUIRED_FIELDS,
  OfferLetterChecklistStep,
  OlvFieldKey
} from 'src/features/proofs/offerLetterChecklistConfig';
import { IProof } from 'src/ts/interfaces';
import { normalizePhoneToE164 } from '../helpers/contactPhone';

export type CheckStatus = 'unset' | 'pass' | 'fail';
export type ProcessStatus = 'active' | 'stopped_edited' | 'stopped_insufficient';

// --- sessionStorage persistence helpers ---

// ITL-3021: -v2- so persisted 9-step state from the old panel can't restore
// into the single-page shape (stale keys just fall back to a clean re-seed).
const OLV_STORAGE_PREFIX = 'offer-letter-verification-v2-';

export interface OlvPersistedState {
  checks: Record<string, CheckStatus>;
  fieldValues?: Record<string, string>;
  compensationData?: CompensationData | null;
  contactsData?: ReviewContact[] | null;
}

export function getPersistedState(proofId: string): OlvPersistedState | null {
  try {
    const raw = window.sessionStorage.getItem(`${OLV_STORAGE_PREFIX}${proofId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Shape guard: checks must be a plain object
    if (!parsed || typeof parsed !== 'object' || typeof parsed.checks !== 'object') return null;
    // Validate contactsData shape if present
    if (parsed.contactsData && !Array.isArray(parsed.contactsData)) return null;
    return parsed as OlvPersistedState;
  } catch {
    return null;
  }
}

export function setPersistedState(proofId: string, state: OlvPersistedState): void {
  try {
    window.sessionStorage.setItem(`${OLV_STORAGE_PREFIX}${proofId}`, JSON.stringify(state));
  } catch {
    // sessionStorage may be unavailable (private mode) or quota exceeded — degrade silently
  }
}

export function clearPersistedState(proofId: string): void {
  try {
    window.sessionStorage.removeItem(`${OLV_STORAGE_PREFIX}${proofId}`);
  } catch {
    // degrade silently
  }
}

// --- Checklist state ---

interface ChecklistState {
  checks: Record<string, CheckStatus>;
  fieldValues: Record<string, string>;
  compensationData: CompensationData | null;
  contactsData: ReviewContact[] | null;
}

// ITL-2875: a non-null `period` is required so the analyst cannot pass
// compensation while the pay frequency is unset (the AI pipeline stores null
// when the cadence is undeterminable; the analyst must resolve it).
export function isCompensationValid(data: CompensationData | null | undefined): boolean {
  return !!(data?.amount && data?.period);
}

// ITL-2932: mirror the backend write-path `normalize_comp_map` on the FE read
// path. A pre-migration (or Trigo-passthrough/backfill) row can still be shaped
// `{type, period: "hour"}`; without this the period <select> has no "hour"
// option so the value renders as the unselected placeholder and the Hours/week
// field never shows (hiding data), while resubmit strips `hours_per_week`
// because `period !== 'hourly'`. Strip the dead `type` key and coerce the
// legacy "hour" spelling to "hourly".
// REMOVE AFTER ITL-2932 is deployed and existing rows are migrated.
export function normalizeCompensation(comp: CompensationData): CompensationData {
  const { type: _type, ...rest } = comp as CompensationData & { type?: unknown };
  return (rest.period as string) === 'hour' ? { ...rest, period: 'hourly' } : rest;
}

type ChecklistAction =
  | { type: 'SET_CHECK'; stepId: string; value: CheckStatus }
  | { type: 'SET_FIELD'; fieldKey: string; value: string }
  | { type: 'SET_COMPENSATION'; value: CompensationData | null }
  | { type: 'SET_CONTACTS'; value: ReviewContact[] | null }
  | { type: 'RESET' }
  | {
      type: 'RESTORE';
      checks: Record<string, CheckStatus>;
      fieldValues: Record<string, string>;
      compensationData?: CompensationData | null;
      contactsData?: ReviewContact[] | null;
    }
  | { type: 'SEED_LLM'; fieldValues: Record<string, string> }
  | { type: 'SEED_COMPENSATION'; value: CompensationData }
  | { type: 'SEED_CONTACTS'; value: ReviewContact[] };

function checklistReducer(state: ChecklistState, action: ChecklistAction): ChecklistState {
  switch (action.type) {
    case 'SET_CHECK':
      return { ...state, checks: { ...state.checks, [action.stepId]: action.value } };
    case 'SET_FIELD':
      return { ...state, fieldValues: { ...state.fieldValues, [action.fieldKey]: action.value } };
    case 'SET_COMPENSATION':
      return { ...state, compensationData: action.value };
    case 'SET_CONTACTS':
      return { ...state, contactsData: action.value };
    case 'RESET':
      return { checks: {}, fieldValues: {}, compensationData: null, contactsData: null };
    case 'RESTORE':
      return {
        checks: action.checks,
        fieldValues: action.fieldValues,
        compensationData: action.compensationData ?? null,
        contactsData: action.contactsData ?? null
      };
    case 'SEED_LLM': {
      // Only seed fields that aren't already set (don't overwrite analyst edits from sessionStorage)
      const merged = { ...action.fieldValues };
      for (const [key, val] of Object.entries(state.fieldValues)) {
        if (val) merged[key] = val;
      }
      return { ...state, fieldValues: merged };
    }
    case 'SEED_COMPENSATION':
      // Only seed if not already set (don't overwrite analyst edits from sessionStorage)
      return state.compensationData ? state : { ...state, compensationData: action.value };
    case 'SEED_CONTACTS':
      // Only seed if not already set (don't overwrite analyst edits from sessionStorage)
      return state.contactsData ? state : { ...state, contactsData: action.value };
  }
}

// --- Process status computation ---

// `stopped_insufficient` remains a valid ProcessStatus — the panel derives it
// from the zero-contacts state — but no checklist step produces it anymore.
function computeProcessStatus(
  checks: Record<string, CheckStatus>,
  steps: OfferLetterChecklistStep[]
): { processStatus: ProcessStatus; stoppedAtStepId: string | null } {
  for (const step of steps) {
    if (checks[step.id] === 'fail' && step.failBehavior === 'EDITED') {
      return { processStatus: 'stopped_edited', stoppedAtStepId: step.id };
    }
  }
  return { processStatus: 'active', stoppedAtStepId: null };
}

// --- Hook ---

export function useOfferLetterChecklist(currentProof: IProof | undefined) {
  const [state, dispatch] = useReducer(checklistReducer, {
    checks: {},
    fieldValues: {},
    compensationData: null,
    contactsData: null
  });
  // ITL-3021: fieldValues keys the AI extraction left blank, decided once per
  // proof at seed time (null until the fetch settles). Keyed off the AI row —
  // not current fieldValues — so a required input stays rendered while the
  // analyst types into it. An analyst-filled value persists to sessionStorage
  // while the AI row stays blank, so this survives refresh correctly.
  const [aiBlankFields, setAiBlankFields] = useState<OlvFieldKey[] | null>(null);
  // ITL-3094: the latest displayable verification row is analyst-authored, i.e.
  // this proof was already submitted; the panel renders the submitted state.
  const [isAnalystSubmitted, setIsAnalystSubmitted] = useState(false);

  // Restore from sessionStorage on proof change
  useEffect(() => {
    setAiBlankFields(null);
    setIsAnalystSubmitted(false);
    if (!currentProof?.id) {
      dispatch({ type: 'RESET' });
      return;
    }
    const persisted = getPersistedState(currentProof.id);
    if (persisted?.checks && Object.keys(persisted.checks).length > 0) {
      dispatch({
        type: 'RESTORE',
        checks: persisted.checks,
        fieldValues: persisted.fieldValues ?? {},
        // ITL-2932: a sessionStorage entry written by pre-migration code can
        // still hold the legacy `{type, period:"hour"}` shape. RESTORE fires
        // before the getVerification seed and wins (SEED_COMPENSATION no-ops
        // once compensationData is set), so normalize here too or the legacy
        // shape reaches the panel unfixed during the deploy window.
        compensationData: persisted.compensationData
          ? normalizeCompensation(persisted.compensationData)
          : null,
        contactsData: persisted.contactsData ?? null
      });
    } else {
      dispatch({ type: 'RESET' });
    }
  }, [currentProof?.id]);

  // Fetch LLM data from verification endpoint
  useEffect(() => {
    if (!currentProof?.id) return;

    let cancelled = false;

    proofApi
      .getVerification(currentProof.id)
      .then((verification) => {
        if (cancelled) return;
        // ITL-3094: analyst rows seed the panel too — they carry the full
        // submitted field values and mark the proof as already verified.
        const isAnalystRow = verification?.source === 'analyst';
        if (
          verification &&
          (isAnalystRow ||
            (verification.source === 'ai_pipeline' &&
              verification.ai_pipeline_status === 'completed'))
        ) {
          setIsAnalystSubmitted(isAnalystRow);
          const llmFields: Record<string, string> = {};
          for (const [fieldKey, responseKey] of Object.entries(OLV_LLM_FIELD_MAP)) {
            const value = verification[responseKey as keyof typeof verification];
            if (typeof value === 'string' && value) {
              llmFields[fieldKey] = value;
            }
          }
          const blanks = (Object.keys(OLV_LLM_FIELD_MAP) as OlvFieldKey[]).filter(
            (k) => !llmFields[k]
          );
          setAiBlankFields(blanks);
          dispatch({ type: 'SEED_LLM', fieldValues: llmFields });

          // ITL-3021: blank rate on completed extractions is the leading
          // indicator that the "extraction is good enough to not re-verify"
          // premise is decaying — no analyst reads these fields anymore, so
          // nothing else would surface it. Field KEYS only, never values.
          if (blanks.length > 0 && !isAnalystRow) {
            datadogLogs.logger.info('OLV AI extraction left fields blank', {
              event: 'offer_letter.verification.blank_fields',
              proof_id: currentProof.id,
              entry_id: currentProof.entry_id,
              blank_fields: blanks,
              blank_required_fields: blanks.filter((k) =>
                OLV_REQUIRED_FIELDS.some((f) => f.key === k)
              )
            });
          }

          const normalizedComp =
            verification.compensation && typeof verification.compensation === 'object'
              ? normalizeCompensation(verification.compensation as CompensationData)
              : null;
          if (normalizedComp) {
            dispatch({ type: 'SEED_COMPENSATION', value: normalizedComp });
          }

          let reviewContacts: ReviewContact[] | null = null;
          if (
            verification.contacts &&
            Array.isArray(verification.contacts) &&
            verification.contacts.length > 0
          ) {
            reviewContacts = verification.contacts.map((c) => ({
              name: c.name ?? null,
              type: c.type ?? null,
              // ITL-2880: strip nulls at the seed so `email` stays co-indexed with
              // field_checks.email and never reaches the render-path `null.split`.
              email: c.email?.filter((e) => e != null) ?? null,
              // ITL-2807: AI-pipeline contacts are intentionally not E.164-normalized
              // at persist time, so normalize on load. Otherwise a valid US number in
              // national format ("(415) 555-2671") would trip the submit guard until
              // the analyst manually blurred the field. Unparseable values pass through
              // unchanged and stay flagged.
              phone: normalizePhoneToE164(c.phone),
              source: c.source ?? null,
              rank: c.rank ?? null,
              quality_score: c.quality_score ?? null,
              is_in_offer_letter: c.is_in_offer_letter ?? false,
              is_verified: false
            }));
            dispatch({ type: 'SEED_CONTACTS', value: reviewContacts });
          }

          // Persist all seeded values in ONE read-merge-write so the seeds
          // survive refresh. Analyst state already in storage wins (same
          // don't-overwrite rule the SEED_* reducer cases apply).
          const existing = getPersistedState(currentProof.id);
          setPersistedState(currentProof.id, {
            ...existing,
            checks: existing?.checks ?? {},
            fieldValues: { ...llmFields, ...(existing?.fieldValues ?? {}) },
            compensationData: existing?.compensationData ?? normalizedComp,
            contactsData: existing?.contactsData ?? reviewContacts
          });
        } else {
          // No completed AI extraction (missing row, in-progress, or failed
          // pipeline) — every mapped field is blank for required-input purposes.
          setAiBlankFields(Object.keys(OLV_LLM_FIELD_MAP) as OlvFieldKey[]);
        }
      })
      .catch((error: unknown) => {
        // getVerification handles 404 by resolving null, so this branch is a
        // real failure (network/5xx). Degrade to all-blank so the analyst can
        // still fill the required fields, and log so a transient-error spike
        // is distinguishable from genuinely blank extractions in Datadog.
        // Never log error.response.data (may carry employer PII).
        if (!cancelled) {
          datadogLogs.logger.warn('OLV verification fetch failed — treating fields as blank', {
            event: 'offer_letter.verification.fetch_failed',
            proof_id: currentProof.id,
            entry_id: currentProof.entry_id,
            status: isAxiosError(error) ? error.response?.status : undefined,
            error_type: error instanceof Error ? error.name : typeof error
          });
          setAiBlankFields(Object.keys(OLV_LLM_FIELD_MAP) as OlvFieldKey[]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentProof?.id]);

  const setCheck = useCallback(
    (stepId: string, value: CheckStatus) => {
      dispatch({ type: 'SET_CHECK', stepId, value });
      if (currentProof?.id) {
        const existing = getPersistedState(currentProof.id);
        setPersistedState(currentProof.id, {
          ...existing,
          checks: { ...(existing?.checks ?? {}), [stepId]: value }
        });
      }
    },
    [currentProof?.id]
  );

  const setFieldValue = useCallback(
    (fieldKey: string, value: string) => {
      dispatch({ type: 'SET_FIELD', fieldKey, value });

      if (currentProof?.id) {
        const existing = getPersistedState(currentProof.id);
        setPersistedState(currentProof.id, {
          ...existing,
          checks: existing?.checks ?? {},
          fieldValues: { ...(existing?.fieldValues ?? {}), [fieldKey]: value }
        });
      }
    },
    [currentProof?.id]
  );

  const setContacts = useCallback(
    (value: ReviewContact[] | null) => {
      dispatch({ type: 'SET_CONTACTS', value });
      if (currentProof?.id) {
        const existing = getPersistedState(currentProof.id);
        setPersistedState(currentProof.id, {
          ...existing,
          checks: existing?.checks ?? {},
          contactsData: value
        });
      }
    },
    [currentProof?.id]
  );

  const { processStatus, stoppedAtStepId } = computeProcessStatus(
    state.checks,
    OFFER_LETTER_CHECKLIST_STEPS
  );

  // ITL-3021: every remaining step (threat_list) must be explicitly passed;
  // required-field and contact gating live in the panel.
  const isReadyToSubmit =
    processStatus === 'active' &&
    OFFER_LETTER_CHECKLIST_STEPS.every((s) => state.checks[s.id] === 'pass');

  return {
    checks: state.checks,
    fieldValues: state.fieldValues,
    compensationData: state.compensationData,
    contactsData: state.contactsData,
    setCheck,
    setFieldValue,
    setContacts,
    processStatus,
    stoppedAtStepId,
    isReadyToSubmit,
    aiBlankFields,
    isAnalystSubmitted
  };
}
